<template>
  <div class="app-notification-wrapper" ref="notificationWrapper">
    <div class="toast-stage" :style="toastStyle">
      <transition name="fade-slide-left" mode="out-in">
        <div
          v-if="showLatestNotification && latestNotification"
          class="toast-inline"
          :key="new Date().getTime()"
        >
          <div class="toast-icon">
            <v-icon :style="{ color: getNotificationColorCode(latestNotification.type) }">
              {{ getNotificationIcon(latestNotification.type) }}
            </v-icon>
          </div>
          <div class="toast-content">
            <div class="toast-title">
              {{ latestNotification.title || getDefaultTitle(latestNotification.type) }}
            </div>
            <div class="toast-message">
              {{ latestNotification.message || getDefaultToastMessage(latestNotification) }}
            </div>
          </div>
        </div>
      </transition>
    </div>

    <v-menu
      bottom
      left
      offset-y
      origin="top right"
      transition="scale-transition"
      width="400"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ attrs, on }">
        <v-btn
          ref="notificationButton"
          class="ml-2"
          min-width="0"
          text
          v-bind="attrs"
          v-on="on"
        >
          <v-badge
            color="success"
            overlap
            bordered
          >
            <template v-if="unreadNotificationCount > 0" v-slot:badge>
              <span>{{ unreadNotificationCount }}</span>
            </template>
            <v-icon class="appbar-icon">mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-list
        :tile="false"
        nav
      >
        <div class="app-notification">
          <div class="notification-header">
            <h3>Thông báo</h3>
            <div class="d-flex align-center">
              <v-btn
                v-if="filteredNotifications.length > 0"
                icon
                x-small
                color="error"
                @click="deleteAllNotifications"
                title="Xóa tất cả thông báo"
                class="mr-3"
              >
                <v-icon size="18">mdi-delete</v-icon>
              </v-btn>

              <v-btn
                v-if="unreadNotificationCount"
                icon
                x-small
                color="success"
                @click="markAllAsRead"
                title="Đánh dấu đã đọc tất cả"
                class="mr-3"
              >
                <v-icon size="18">mdi-check-all</v-icon>
              </v-btn>

              <v-menu
                bottom
                left
                offset-y
              >
                <template v-slot:activator="{ attrs, on }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    class="mr-1"
                    title="Lọc thông báo"
                  >
                    <v-icon size="18">mdi-filter</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="setFilterType('all')">
                    <v-list-item-title :class="{ 'font-weight-bold': filterType === 'all' }">Tất cả</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="setFilterType('unread')">
                    <v-list-item-title :class="{ 'font-weight-bold': filterType === 'unread' }">Chưa đọc</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
          <div class="notification-height">
            <div
              v-for="(notification, i) in filteredNotifications"
              :key="`app-notification-${i}`"
              class="app-notification-item pa-3"
              :class="{ 'unread-notification': notification.isRead === 0 || notification.is_read === 0 }"
            >
              <div class="d-flex align-start">
                <v-icon :color="getNotificationColor(notification.type)" class="mr-2 mt-1">{{ getNotificationIcon(notification.type) }}</v-icon>
                <div class="flex-grow-1">
                  <div class="font-weight-bold mb-1">{{ notification.title || getDefaultTitle(notification.type) }}</div>
                  <div v-if="notification.message" v-html="notification.message" class="text-caption mb-1"></div>
                  <div v-if="notification.data && notification.data.code" class="text-caption mb-1">Mã: <strong>{{ notification.data.code }}</strong></div>
                  <div v-if="notification.data && notification.data.amount" class="text-caption mb-1">Số tiền: <strong>{{ formatCurrency(notification.data.amount) }} đ</strong></div>
                  <div v-if="notification.type === 'download' && notification.data.urlExport" class="mb-1">
                    <a 
                      href="#" 
                      class="download-link"
                      @click.prevent="handleDownload(notification.data.urlExport, notification.data.fileName)"
                    >
                      Tải xuống
                    </a>
                  </div>
                  <div class="text-caption grey--text">{{ formatTime(notification.createdAt) }}</div>
                </div>
                <v-menu
                  v-if="notification.type !== 'info_excel'"
                  bottom
                  left
                  offset-y
                  class="mark-read-btn"
                >
                  <template v-slot:activator="{ attrs, on }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon size="16">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item 
                      v-if="notification.isRead === 0"
                      @click="markAsRead(notification, i)"
                    >
                      <v-list-item-title>Đánh dấu đã đọc</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="deleteNotification(notification)">
                      <v-list-item-title>Xóa thông báo này</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <div class="text-center pa-3" v-if="filteredNotifications.length === 0">
              <label class="grey--text">Không có thông báo <b>{{ filterType === 'all' ? '' : 'Chưa đọc' }}</b> nào</label>
            </div>
            <div class="text-center pa-3" v-if="filteredNotifications.length > 0 && paging.nextId">
              <v-btn
                small
                outlined
                color="primary"
                :loading="loadingMore"
                @click="loadMore"
              >
                Tải thêm
              </v-btn>
            </div>
          </div>
        </div>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import * as moment from 'moment'
import { downloadFile } from '@/helpers/utils'
import POSNotificationRepository from '@/repositories/POSNotificationRepository'

export default {
  name: 'AppNotification',
  
  data() {
    return {
      showLatestNotification: false,
      latestNotification: null,
      toastTimeout: null,
      previousNotificationCount: 0,
      isMounted: false,
      lastFirstUid: null,
      id: '',
      backward: false,
      paging: {},
      loadingMore: false,
      filterType: 'all', // 'all' hoặc 'unread'
      params: {
        limit: 20,
      },
    }
  },

  computed: {
    ...mapState({
      notifications: state => state.notifications || [],
      unreadNotificationCount: state => state.unreadNotificationCount || 0
    }),

    toastStyle() {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth
      const toastWidth = Math.min(320, viewportWidth - 32)
      return {
        position: 'fixed',
        top: '24px',
        right: '24px',
        width: `${toastWidth}px`,
        zIndex: 9999,
        pointerEvents: 'none'
      }
    },

    filteredNotifications() {
      if (!this.notifications || !Array.isArray(this.notifications)) return []
      if (this.filterType === 'all') {
        return this.notifications
      }
      return this.notifications.filter(notify => notify.isRead === 0)
    }
  },

  watch: {
    notifications: {
      handler(newVal) {
        if (!this.isMounted) return
        if (!newVal || newVal.length === 0) return

        const first = newVal[0]
        const currentUid = first && first.uid ? first.uid : null
        if (currentUid && currentUid !== this.lastFirstUid) {
          this.showLatestNotification = true
          this.latestNotification = first
          this.lastFirstUid = currentUid

          if (this.toastTimeout) {
            clearTimeout(this.toastTimeout)
          }

          this.$nextTick(() => {
            this.toastTimeout = setTimeout(() => {
              this.hideToast()
            }, 3000)
          })
        }
      },
      immediate: false
    }
  },

  async mounted() {
    await this.fetchNotifications()
    
    if (this.notifications && this.notifications.length) {
      this.lastFirstUid = this.notifications[0] && this.notifications[0].uid ? this.notifications[0].uid : null
    }
    this.$nextTick(() => { this.isMounted = true })
  },

  beforeDestroy() {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout)
    }
  },

  methods: {
    ...mapMutations({
      clearNotifications: 'CLEAR_APP_NOTIFICATIONS',
      setNotifications: 'SET_APP_NOTIFICATIONS',
      appendNotifications: 'APPEND_APP_NOTIFICATIONS'
    }),

    setFilterType(type) {
      this.filterType = type
      // Reset và fetch lại khi đổi filter
      this.paging = {}
      this.fetchNotifications(false)
    },

    async fetchNotifications(append = false) {
      try {
        const params = { ...this.params }
        
        // Thêm filter theo filterType
        if (this.filterType === 'unread') {
          params.is_read = 0
        }
        // Nếu filterType === 'all' thì không thêm is_read, lấy tất cả
        
        if (append && this.paging.nextId) {
          params.id = this.paging.nextId
          params.backward = false
        }

        const response = await POSNotificationRepository.list(params)
        
        if (response && response.data) {
          const responseData = response.data.data
          const notificationsData = responseData.data || []
          const paging = responseData.paging || {}

          if (append) {
            // Append vào danh sách hiện tại
            this.appendNotifications(notificationsData)
          } else {
            // Replace danh sách
            this.setNotifications(notificationsData)
          }
          
          this.paging = paging
          this.id = paging.nextId || ''
          const unreadCount = responseData.unreadCount || 0
          this.$store.commit('SET_UNREAD_NOTIFICATION_COUNT', unreadCount)
        }
      } catch (error) {
        console.error('Error fetching notifications:', error)
        if (!append) {
          this.setNotifications([])
        }
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.paging.nextId) return
      
      this.loadingMore = true
      try {
        await this.fetchNotifications(true)
      } catch (error) {
        console.error('Error loading more notifications:', error)
      } finally {
        this.loadingMore = false
      }
    },

    formatCurrency(amount) {
      if (!amount) return '0'
      return new Intl.NumberFormat('vi-VN').format(amount)
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      return moment(timestamp).format('DD/MM/YYYY HH:mm:ss')
    },

    getNotificationColor(type) {
      const colorMap = {
        'payment': 'success',
        'success': 'success',
        'error': 'error',
        'warning': 'warning',
        'info': 'info',
        'download': 'info',
        'info_excel': 'info'
      }
      return colorMap[type] || 'info'
    },

    getNotificationIcon(type) {
      const iconMap = {
        'payment': 'mdi-check-circle',
        'success': 'mdi-check-circle',
        'error': 'mdi-alert-circle',
        'warning': 'mdi-alert',
        'info': 'mdi-information',
        'download': 'mdi-download',
        'info_excel': 'mdi-information'
      }
      return iconMap[type] || 'mdi-information'
    },

    getDefaultTitle(type) {
      const titleMap = {
        'payment': 'Thanh toán thành công',
        'success': 'Thành công',
        'error': 'Lỗi',
        'warning': 'Cảnh báo',
        'info': 'Thông báo',
        'download': 'Tải xuống',
        'info_excel': 'Thông báo'
      }
      return titleMap[type] || 'Thông báo'
    },

    getDefaultToastMessage(notification) {
      if (!notification) return ''
      if (notification.message) return notification.message
      const { code } = notification
      if (code) {
        return `Mã #${code}`
      }
      return this.getDefaultTitle(notification.type)
    },

    getNotificationColorCode(type) {
      const colorMap = {
        'payment': '#2e7d32',
        'success': '#2e7d32',
        'error': '#d32f2f',
        'warning': '#ed6c02',
        'info': '#0288d1',
        'download': '#0288d1',
        'info_excel': '#0288d1'
      }
      return colorMap[type] || '#0288d1'
    },

    async markNotificationsAsRead(ids) {
      try {
        if (!ids) {
          console.error('Notification IDs are missing')
          return
        }

        await POSNotificationRepository.markAsRead({ ids })
        
        // Cập nhật local state
        const updatedNotifications = this.notifications.map(notify => {
          const notificationId = notify._id
          if (ids === 'all') {
            return {
              ...notify,
              isRead: 1,
            }
          }
          if (ids.includes(notificationId)) {
            return {
              ...notify,
              isRead: 1,
            }
          }
          return notify
        })

        if (ids === 'all') {
          this.$store.commit('SET_UNREAD_NOTIFICATION_COUNT', 0)
        } else {
          const markedCount = ids.length
          const newUnreadCount = Math.max(0, this.unreadNotificationCount - markedCount)
          this.$store.commit('SET_UNREAD_NOTIFICATION_COUNT', newUnreadCount)
        }

        this.setNotifications(updatedNotifications)
      } catch (error) {
        console.error('Error marking notifications as read:', error)
      }
    },

    async markAsRead(notification, index) {
      const notificationId = notification._id
      if (!notificationId) {
        console.error('Notification ID is missing')
        return
      }
      await this.markNotificationsAsRead([notificationId])
    },

    async markAllAsRead() {
      await this.markNotificationsAsRead('all')
    },

    async deleteAllNotifications() {
      this.$swal({
        title: 'Xóa tất cả thông báo',
        text: 'Bạn có chắc chắn muốn xóa tất cả thông báo?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Đồng ý',
        cancelButtonText: 'Hủy bỏ',
      }).then(async (result) => {
        if (result.value) {
          try {
            await POSNotificationRepository.deleteAll()
            // Xóa tất cả thông báo khỏi local state
            this.setNotifications([])
            this.$store.commit('SET_UNREAD_NOTIFICATION_COUNT', 0)
            this.$swal({
              title: 'Đã xóa thành công!',
              type: 'success',
              confirmButtonText: 'Đồng ý',
            })
          } catch (error) {
            console.error('Error deleting all notifications:', error)
            this.$swal({
              type: 'error',
              title: 'Lỗi!',
              text: 'Có lỗi xảy ra khi xóa thông báo',
            })
          }
        }
      })
    },

    async deleteNotification(notification) {
      const notificationId = notification._id
      if (!notificationId) {
        console.error('Notification ID is missing')
        return
      }

      try {
        await POSNotificationRepository.delete(notificationId)
        
        const isUnread = notification.isRead === 0
        if (isUnread && this.unreadNotificationCount > 0) {
          const newUnreadCount = Math.max(0, this.unreadNotificationCount - 1)
          this.$store.commit('SET_UNREAD_NOTIFICATION_COUNT', newUnreadCount)
        }
        
        // Cập nhật local state - xóa thông báo khỏi danh sách
        const updatedNotifications = this.notifications.filter(notify => notify._id !== notificationId)
        this.setNotifications(updatedNotifications)
      } catch (error) {
        console.error('Error deleting notification:', error)
      }
    },

    hideToast() {
      this.showLatestNotification = false
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout)
        this.toastTimeout = null
      }
    },

    handleDownload(urlExport, fileName) {
      if (urlExport) {
        downloadFile(urlExport, fileName || 'download.xlsx')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-notification-wrapper {
  display: flex;
  align-items: center;
  position: relative;

  .notification-toast {
    position: absolute;
    top: 100%;
    right: 0;
    width: 400px;
    z-index: 9999;
  }
}

.app-notification {
  width: 400px;
}

.notification-header {
  padding: 5px 15px;
  color: #a847bf;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification-height {
  max-height: 400px;
  overflow-y: auto;
}

.app-notification-item {
  border-bottom: 1px solid #ececec;
  
  &:hover {
    background-color: #efefef !important;
  }
  
  &:last-child {
    border-bottom: none;
  }

  &.unread-notification {
    background-color: #eef8ff;
    
    &:hover {
      background-color: #ededed !important;
    }
  }

  .mark-read-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .mark-read-btn {
    opacity: 1;
  }
}

.appbar-icon {
  font-size: 22px !important;
}

.notification-toast {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.notification-toast-container {
  width: 400px;
}

.toast-stage {
  pointer-events: none;
}

.toast-inline {
  width: 100%;
  background: #fff;
  color: #111;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
}

.toast-icon {
  margin-right: 12px;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-icon .v-icon {
  font-size: 28px;
}

.toast-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.toast-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.toast-message {
  color: #111;
}

.fade-slide-left-enter-active,
.fade-slide-left-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-slide-left-enter,
.fade-slide-left-leave-to {
  opacity: 0;
  transform: translateX(16px);
}

.fade-slide-left-enter-to,
.fade-slide-left-leave {
  opacity: 1;
  transform: translateX(0);
}

.download-link {
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    color: #1565c0;
    text-decoration: underline;
  }
  
  &:active {
    color: #0d47a1;
  }
}
</style>

